import stringify from 'fast-safe-stringify'

import { getErrorMessageByStatusCode } from '~/hooks/tanstack-query'

import { dispatchErrorToast } from '../slices/toasterSlice'
import { useStore } from '../store'
import { AsyncFn } from './utils'

/**
 * Middleware that creates entities
 */
const onGoingCreations = new Map<string, Promise<void>>()

export function withCreate<T extends AsyncFn>(call: T): (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>> {
    return async (...args: Parameters<T>) => {
        const [entity] = args

        // in case if we passed empty data into batch creation
        if (entity.length === 0) {
            return
        }

        const currentKey = `${call.name}__${stringify(entity)}`
        if (onGoingCreations.has(currentKey)) return onGoingCreations.get(currentKey)

        function onFailure(statusCode?: number) {
            dispatchErrorToast({ message: getErrorMessageByStatusCode(statusCode) })
        }

        const promise = call(...args)
        onGoingCreations.set(currentKey, promise)

        let response
        try {
            response = await promise
        } catch (e) {
            onFailure()
            throw e
        } finally {
            onGoingCreations.delete(currentKey)
        }

        if (response?.response?.status === 200) {
            const data = Array.isArray(response.data) ? response.data : [response.data]
            useStore.getState().di.actions.addEntities(data)
        } else {
            onFailure(response?.response?.status)
        }

        return response
    }
}
